<template>
  <div>
    <v-window
      v-if="! dialog || ! editable"
      v-model="view"
      touchless
    >
      <v-window-item>
        <h-dashboard-list
          @preview="preview"
        />
      </v-window-item>
      <v-window-item>
        <h-dashboard-rooms
          @book="book"
          @create="create"
          @preview="preview"
          @edit="edit"
        />
      </v-window-item>
    </v-window>
    <v-fab-transition>
      <v-btn
        v-show="showReload"
        fab
        fixed
        bottom
        right
        color="primary"
        :loading="synchronizing"
        @click="syncOrders()"
      >
        <v-icon v-text="icons.reload" />
      </v-btn>
    </v-fab-transition>
    <v-dialog
      v-model="dialog"
      :fullscreen="editable"
      scrollable
      persistent
      no-click-animation
      :transition="editable ? 'dialog-bottom-transition' : 'dialog-transition'"
      @click:outside="handleDialogClickOutside"
    >
      <h-order
        :visible="dialog"
        :editable="editable"
        @edit="edit"
        @close="close"
        @finish="finish"
      />
    </v-dialog>
    <h-confirm
      v-model="lockedDialog"
      :title="__('error')"
      :text="[__('lockedByAnother'), __('saveFailedDesc')]"
      @yes="checkIfLockedByMe()"
      @no="closeDialog()"
    />
    <h-confirm
      v-model="showConfirmTurn"
      :title="confirmTurnText"
      :yes="turn ? 'closeTurn' : 'openTurn'"
      no="cancel"
      @yes="confirmedTurn()"
      @no="showConfirmTurn = false"
    />
    <h-turn />
    <h-in-out />
    <h-report />
    <h-settings />
    <h-purchase />
    <h-fiscal-info />
    <h-help />
    <h-history />
    <h-quick-guide />
    <h-tour
      v-model="showTour"
      :finishing="finishing"
    />
    
    <!-- Global click blocker overlay - stays fixed while dialogs close -->
    <h-click-blocker
      v-model="dialogClosing"
      :timeout="500"
      transparent
    />
  </div>
</template>

<script>
  import moment from 'moment'
  import { mapState, mapGetters } from 'vuex'
  import layout from '@/Layouts/App'
  import Configurable from '@/Mixins/Configurable'
  import I18n from '@/Mixins/I18n'
  import InteractsWithLoading from '@/Mixins/InteractsWithLoading'
  import ManagesTurns from '@/Mixins/ManagesTurns'
  import Synchronizable from '@/Mixins/Synchronizable'
  import ShowsTour from '@/Mixins/ShowsTour'
  import Userable from '@/Mixins/Userable'
  import UsesIcons from '@/Mixins/UsesIcons'
  import OrderStatus from '@/Models/Static/OrderStatus'
  import HClickBlocker from '@/Components/ClickBlocker'
  import HConfirm from '@/Components/Messages/Confirm'
  import HDashboardList from '@/Sections/Dashboard/List'
  import HDashboardRooms from '@/Sections/Dashboard/Rooms/Card'
  import HFiscalInfo from '@/Sections/FiscalInfo/Dialog'
  import HHelp from '@/Sections/Help/Dialog'
  import HHistory from '@/Sections/History/Dialog'
  import HInOut from '@/Sections/CashControl/InOut/Dialog'
  import HOrder from '@/Sections/Order/Card'
  import HPurchase from '@/Sections/Purchase/Dialog'
  import HQuickGuide from '@/Sections/Help/QuickGuide'
  import HReport from '@/Sections/CashControl/Report/Dialog'
  import HSettings from '@/Sections/Settings/Dialog'
  import HTurn from '@/Sections/CashControl/Turn/Dialog'
  import HTour from '@/Components/Tour/Dashboard'

  export default {
    layout,

    mixins: [
      Configurable,
      I18n,
      InteractsWithLoading,
      ManagesTurns,
      Synchronizable,
      ShowsTour,
      Userable,
      UsesIcons,
    ],

    components: {
      HClickBlocker,
      HConfirm,
      HDashboardList,
      HDashboardRooms,
      HFiscalInfo,
      HHelp,
      HHistory,
      HInOut,
      HOrder,
      HPurchase,
      HQuickGuide,
      HReport,
      HSettings,
      HTour,
      HTurn,
    },

    data: () => ({
      OrderStatus,
      showReload: true,
      editable: false,
      dialog: false,
      finishing: false,
      lockedDialog: false,
      checkingLock: false,
      dialogClosing: false,
      snackbarClosing: false,
    }),

    computed: {
      ...mapState([
        'UUID',
      ]),
      view: {
        get () {
          return this.$store.state.dashboard.view
        },
        set (v) {
          this.$store.commit('dashboard/view', v)
        },
      },
      ...mapState('orders', {
        order: 'current',
        locked: 'locked',
      }),
      ...mapState('cashControl', {
        confirmTurn: 'confirm',
      }),
      ...mapState('workingModes', {
        tpvMode: 'tpv',
      }),
      ...mapGetters('rummage', [
        'cashier',
      ]),
      confirmTurnText () {
        return this.__(this.confirmTurn) ?? ''
      },
      showConfirmTurn: {
        get () {
          return this.confirmTurn !== null
        },
        set (v) {
          this.$store.commit('cashControl/confirm', v || null)
        },
      },
      dishOrders () {
        return this.$store.getters['catalog/activeDishOrders']
      },
      firstDishOrder () {
        return this.dishOrders[0]?.order ?? 0
      },
      lastDishOrder () {
        return this.order.orderContents.pluck('dishOrder').sort().last()
      },
      dishOrder () {
        return this.config('rememberDishOrder') && this.dishOrders.some(o => o.order == this.lastDishOrder)
          ? this.lastDishOrder
          : this.firstDishOrder
      },
      autoClose () {
        return this.order.isOpen && this.finishing && ! this.config('keepOrdersOpen')
      },
    },

    watch: {
      view () {
        window.scrollTo(0, 0)
      },
      tpvMode (v) {
        if (v) {
          this.tpv()
        }
      },
    },

    mounted () {
      // Listen for global event to activate overlay when any dialog starts closing
      this.$root.$on('fullscreen-dialog-start-closing', this.activateClosingOverlay)
      // Listen for snackbar closing events
      this.$root.$on('snackbar-closing', this.onSnackbarClosing)
      this.$root.$on('snackbar-closed', this.onSnackbarClosed)
    },

    beforeDestroy () {
      // Clean up event listeners
      this.$root.$off('fullscreen-dialog-start-closing', this.activateClosingOverlay)
      this.$root.$off('snackbar-closing', this.onSnackbarClosing)
      this.$root.$off('snackbar-closed', this.onSnackbarClosed)
    },

    methods: {
      activateClosingOverlay () {
        this.dialogClosing = true
      },
      open (nestedDialog) {
        this.dialog = true
        this.showReload = false
        if (nestedDialog) {
          setTimeout(() => this.$store.dispatch('orders/dialogs/set', nestedDialog), 75)
        }
      },
      preview (id) {
        if (this.loading || this.locked) {
          return
        }
        this.showReload = false
        this.startLoading()
        this.$log('Previewing...', 'Order', id)
        this.$api('getOrder', {id})
        .then(r => {
          this.$log('Preview', 'Order', id)
          this.$store.commit('orders/current', r.data?.order)
            if (this.$store.getters['orders/usesWeight']) {
              this.$store.dispatch('alert/showWarning', this.__('orderContainsWeightProducts'))
              this.showReload = true
              return
            }
            this.editable = false
            this.open()
          })
          .catch(e => this.$store.dispatch('xError', e))
          .finally(() => this.stopLoading())
      },
      edit (nestedDialog) {
        if (this.loading || this.locked) {
          return
        }
        // Capture orderId in local variable for consistent logging
        const orderId = this.order.id
        
        this.startLoading()
        this.$log('Editing...', 'Order', orderId)
        this.$api('lockOrder', {
          orderId: orderId,
          uuid: this.UUID,
        })
          .then(r => {
            if (! r.data?.success) {
              const deviceName = r.data?.deviceName ?? 'Unknown'
              this.$log(`Locked by ${deviceName}`, 'Order', orderId)
              this.$store.dispatch('alert/showError', this.__('lockedBy') + `: ${deviceName}`)
              return
            }
            const order = r.data.order
            if (order.status == this.OrderStatus.PENDING_NOTE) {
              order.status = this.OrderStatus.NORMAL
            }
            this.$store.commit('orders/current', order)
            if (this.$store.getters['orders/usesWeight']) {
              this.$log('Contains weight products', 'Order', order.id)
              this.closeDialog()
              this.$store.dispatch('alert/showWarning', this.__('orderContainsWeightProducts'))
              this.unlock(order.id).catch(e => AndroidDevice.warn(`Cant't UNLOCK the order: ${e.response?.data?.message}`))
              return
            }
            if (order.closed !== null) {
              this.$log('Already closed', 'Order', order.id)
              this.closeDialog()
              this.$store.dispatch('alert/showWarning', this.__('orderAlreadyClosed'))
              this.$store.commit('orders/remove', order.id)
              this.unlock(order.id).catch(e => AndroidDevice.warn(`Cant't UNLOCK the order: ${e.response?.data?.message}`))
              return
            }
            this.$store.commit('orders/dishOrder', this.dishOrder)
            this.$store.dispatch('orders/payment/reset')
            this.$log('Edit', 'Order', order.id)
            this.$store.commit('sync/skip')
            this.editable = true
            this.open(nestedDialog)
          })
          .catch(e => this.$store.dispatch('xError', e))
          .finally(() => this.stopLoading())
      },
      book (ev) {
        this.startLoading()
        this.$log('Booking...', 'Table', ev.table_id)
        this.$api(['getNewId', 'Order'], {
          tenantUserId: this.user.id,
          lockedBy: this.UUID,
          opened: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
          table_id: ev.table_id,
          guests: ev.guests,
          status: this.OrderStatus.PENDING_NOTE,
        })
          .then(r => {
            this.$log('Created empty order', 'Order', r.data.id)
            this.syncOrders()
            this.unlock(r.data.id).catch(e => AndroidDevice.warn(`Cant’t UNLOCK the order: ${e.response?.data?.message}`))
          })
          .catch(e => this.$store.dispatch('xError', e))
          .finally(() => this.stopLoading())
      },
      create (ev) {
        const opened = moment().utc().format('YYYY-MM-DD HH:mm:ss')
        this.startLoading()
        this.$store.commit('sync/skip')
        this.$log('Ordering...', 'Table', ev.table.id)
        this.$api(['getNewId', 'Order'], {
          tenantUserId: this.user.id,
          lockedBy: this.UUID,
          opened: opened,
          tableName: ev.table.name,
          table_id: ev.table.id,
          guests: ev.guests,
        })
          .then(r => {
            this.$log('Created empty order', 'Order', r.data.id)
            this.$store.commit('orders/current', {
              id: r.data.id,
              guests: ev.guests,
              lockedBy: this.UUID,
              opened: opened,
              tableName: ev.table.name,
              table_id: ev.table.id,
              tenantUser_id: this.user.id,
              tenantUserName: this.user.name,
            })
            this.$store.commit('orders/dishOrder', this.dishOrder)
            this.editable = true
            this.open('catalog')
          })
          .catch(e => {
            this.$store.commit('sync/skip', false)
            this.$store.dispatch('xError', e)
          })
          .finally(() => this.stopLoading())
      },
      tpv () {
        this.create({
          guests: 1,
          table: {
            id: null,
            name: this.__('tpv') + ' - ' + this.user.name,
          },
        })
      },
      finish () {
        if (this.locked) {
          return
        }
        // Capture orderId in local variable for consistent logging
        const orderId = this.order.id
        
        this.startLoading()
        this.$log('Closing...', 'Order', orderId)
        this.$api('lockOrder', {
          orderId: orderId,
          uuid: this.UUID,
        })
          .then(r => {
            if (r.data?.success) {
              this.finishing = true
              this.$store.commit('orders/close')
              this.freeSaveOrUnlock()
            }
          })
          .catch(e => this.$store.dispatch('xError', e))
      },
      handleDialogClickOutside (event) {
        if (this.snackbarClosing) {
          return
        }
        this.close()
      },
      onSnackbarClosing () {
        this.snackbarClosing = true
      },
      onSnackbarClosed () {
        this.snackbarClosing = false
      },
      close (finish = false) {
        if (this.locked) {
          return
        }
        if (! this.editable && ! finish) {
          this.showReload = true
          this.dialog = false
          return
        }
        this.finishing = finish
        this.checkIfLockedByMe()
      },
      checkIfLockedByMe () {
        // Capture orderId FIRST before any async operations
        const orderId = this.order.id
        
        // Prevent duplicate calls while one is already in progress
        if (this.checkingLock) {
          this.$log('Already checking lock, ignoring duplicate call', 'Order', orderId)
          return
        }
        
        this.checkingLock = true
        this.lockedDialog = false
        this.startLoading()
        this.$store.commit('orders/lock')
        this.$log('Checking if locked by me...', 'Order', orderId)
        this.$api('isLockedByMe2Order', {
          orderId: orderId,
          uuid: this.UUID,
        })
          .then(r => {
            this.$log(`Locked by me: ${r.data.lockedByMe}`, 'Order', orderId)
            
            // Check if current order has changed while waiting for response
            if (this.order.id !== orderId) {
              this.$log('Order changed during check, ignoring result', 'Order', orderId)
              this.stopLoading()
              this.$store.commit('orders/unlock')
              this.checkingLock = false
              return
            }
            
            if (r.data.lockedByMe !== true) {
              this.stopLoading()
              this.$store.commit('orders/unlock')
              this.checkingLock = false
              this.lockedDialog = true
              return
            }
            this.freeSaveOrUnlock()
          })
          .catch(e => {
            // Check if current order has changed while waiting for response
            if (this.order.id !== orderId) {
              this.$log('Order changed during check, ignoring error', 'Order', orderId)
              this.stopLoading()
              this.$store.commit('orders/unlock')
              this.checkingLock = false
              return
            }
            
            this.stopLoading()
            this.$store.commit('orders/unlock')
            this.checkingLock = false
            this.$store.dispatch('xError', e)
          })
      },
      freeSaveOrUnlock () {
        // An order is truly empty only if it has no contents AND no invoices
        // (contents can be moved to invoices during payment)
        const isTrulyEmpty = this.order.isEmpty && 
                            this.order.orderInvoices.length === 0 &&
                            this.order.alreadyPaid === 0
        
        const request = isTrulyEmpty
          ? this.free()
          : this.$store.state.orders.isDirty
            ? this.save()
            : this.unlock()
        request
          .catch(e => this.$store.dispatch('xError', e))
          .finally(() => this.closeDialog())
      },
      async free () {
        return await this.$store.dispatch('orders/free')
      },
      save (lock = false) {
        this.$store.commit('orders/linkInvoicesWithContents')
        if (this.autoClose) {
          this.$store.commit('orders/close')
        }
        this.$log('Saving...', 'Order', this.order.id)
        return this.$api(['save', 'Order'], {
          ...this.order,
          saveToken: String.random(32),
          lockedBy: lock ? this.UUID : null,
          ...(this.cashier.warehouse_id ? { warehouse_id: this.cashier.warehouse_id } : {}),
        })
          .then(r => {
            this.$log('Saved', 'Order', this.order.id)
            if (this.shouldViewTour()) {
              setTimeout(() => this.showTour = true, 250)
            }
            if (this.finishing) {
              localStorage.setItem('firstOrder', 'done')
            }
            if (!! r.data?.closed) {
              this.$store.commit('orders/remove', r.data?.id)
            }
          })
      },
      unlock (id) {
        const orderId = id ?? this.order.id
        this.$log('Unlocking...', 'Order', orderId)
        return this.$api('unlockOrder', { orderId, uuid: this.UUID })
          .then(r => this.$log('Unlocked', 'Order', orderId))
      },
      confirmedTurn () {
        this.showConfirmTurn = false
        this.startLoading()
        this.setTurn().finally(() => {
          this.stopLoading()
          if (! this.needManualTurn && this.turn) {
            this.openPayment()
          }
        })
      },
      openPayment () {
        this.$store.dispatch('orders/payment/reset')
        this.editable
          ? this.$store.dispatch('orders/dialogs/openPayment')
          : this.edit('payment')
      },
      async reset () {
        await this.$store.dispatch('orders/dialogs/close')
        await this.$store.dispatch('catalog/reset')
        this.lockedDialog = false
        this.$store.commit('orders/unlock')
        this.stopLoading()
        this.$store.commit('sync/skip', false)
      },
      async closeDialog () {
        await this.reset()
        if (this.tpvMode) {
          // In TPV mode, keep checkingLock active until new order is created
          // to prevent duplicate calls during order creation
          this.tpv()
          // Reset flag after a small delay to ensure tpv() has started
          setTimeout(() => this.checkingLock = false, 500)
          return
        }
        // Reset checkingLock flag only when dialog is actually closing
        this.checkingLock = false
        this.dialog = false
        setTimeout(() => this.showReload = true, 275)
      },
    },
  }
</script>
